import React from "react";
import { DatePicker, Label } from "@src/common/components";
import { RecurrenceScheduleType } from "@api/graphql/generated/generated-types";
import { getCourierTodayForDatePicker } from "@src/common/utils/timezoneConverters";
import { dayAbbreviations } from "../utils/DateConstants";
import {
  ExtendedRecurrenceScheduleType,
  Week,
} from "../utils/CreateOrderContextProvider";

function StartDateEndDateSelector({
  scheduleDates,
  setScheduleDates,
  recurrenceSchedule,
  everyNWeeksInterval,
  selectedDaysWeek,
}: {
  scheduleDates: { startDate: Date | undefined; endDate: Date | null | undefined };
  setScheduleDates: any;
  recurrenceSchedule: ExtendedRecurrenceScheduleType;
  everyNWeeksInterval: number;
  selectedDaysWeek: Week;
}) {
  const handleStartDateChange = (date) => {
    if (date) {
      setScheduleDates({ ...scheduleDates, startDate: date });
    } else {
      setScheduleDates({ ...scheduleDates, startDate: null });
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      setScheduleDates({ ...scheduleDates, endDate: date });
    } else {
      setScheduleDates({ ...scheduleDates, endDate: null });
    }
  };

  const generateScheduleMessage = () => {
    const { startDate, endDate } = scheduleDates;
    const formattedStartDate = startDate
      ? startDate.toLocaleDateString()
      : "N/A";
    const formattedEndDate = endDate ? endDate.toLocaleDateString() : "N/A";

    switch (recurrenceSchedule) {
      case RecurrenceScheduleType.Daily:
        if (startDate && endDate) {
          return `Daily recurrence between ${formattedStartDate} and ${formattedEndDate}`;
        } else if (startDate) {
          return `Daily recurrence starting ${formattedStartDate}`;
        }
        break;
      case RecurrenceScheduleType.Biweekly:
        if (!startDate) {
          return "Biweekly recurrence not set"; // or a similar message indicating the absence of a start date
        }

        // Use native Date to get weekday name (avoids moment courier TZ interpretation)
        const weekday = startDate.toLocaleDateString("en-US", {
          weekday: "long",
        });

        if (endDate) {
          const formattedEndDate = endDate.toLocaleDateString();
          return `Every other ${weekday}, until ${formattedEndDate}`;
        } else {
          return `Every other ${weekday}`;
        }

      case RecurrenceScheduleType.EveryNWeeks: {
        if (!startDate) {
          return "Every N weeks recurrence not set";
        }
        const dayLabels = Object.entries(selectedDaysWeek || {})
          .filter(([, isSelected]) => isSelected)
          .map(([day]) => dayAbbreviations[day])
          .join(", ");
        const cadence = `Every ${everyNWeeksInterval} weeks${
          dayLabels ? ` on ${dayLabels}` : ""
        }`;
        return endDate
          ? `${cadence}, until ${endDate.toLocaleDateString()}`
          : cadence;
      }

      // Handle other cases if needed
      default:
        return "";
    }
    return "";
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 justify-between">
        <div className="flex-auto flex flex-col">
          <Label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            Schedule Start Date
          </Label>
          <DatePicker
            size="mini"
            minDate={getCourierTodayForDatePicker()}
            value={scheduleDates.startDate}
            onChange={({ date }) => handleStartDateChange(date)}
            clearable={true}
          />
        </div>

        <div className="flex-auto flex flex-col">
          <Label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            Schedule End Date (Optional)
          </Label>
          <DatePicker
            size="mini"
            minDate={getCourierTodayForDatePicker()}
            value={scheduleDates.endDate}
            onChange={({ date }) => handleEndDateChange(date)}
            placeholder="Select end date"
            clearable={true}
          />
        </div>
      </div>

      <p className="text-xs text-yellow-500 dark:text-yellow-400">{generateScheduleMessage()}</p>
    </div>
  );
}

export default StartDateEndDateSelector;
